import {getStore} from "../../../store";
import blockIconsToComponent from "./blockIconsToComponent";
import fillEmptyComponents from "./fillEmptyComponents";
import debounce from '../../utils/debounce';
import extendExternalComponents from './extendExternalComponents';
import {createId} from '../../utils';
import {findOneDepthParent} from '../../index';

export const CMD_SELECT_TARGET = 'select-target';
export const CMD_ACTIVE_STYLES = 'enable-sm';

export default function studioPlugin(editor) {
    const {appEditorStore} = getStore();
    const {editorConfig, projectIdToLoad} = appEditorStore;
    if (editorConfig) {
        const {defaultProject} = editorConfig;

        if (defaultProject) {
            const {data} = defaultProject;
            editor.Pages.getConfig().pages = [...data.pages];
            editor.Css.config.rules = data.styles;
        }
    }

    if (projectIdToLoad) {
        const storageOptions = editor.Storage.getConfig().options;
        if (storageOptions) {
            storageOptions.local.key = appEditorStore.projectIdToLoad;
        }
    }

    // TODO move in grapesjs/react
    const rteConfig = editor.RichTextEditor.getConfig();
    rteConfig.custom = true;

    // B 버튼: 에디터의 일반 스타일 패턴과 동일하게 클래스 셀렉터로 font-weight 500 적용.
    // 인라인 style은 GrapesJS가 #id 규칙으로 바꿔 저장/미리보기에서 누락되고,
    // wrap 등 다른 스타일과 섞이는 원인이 된다.
    // 올바른 형태: .detail_span_xxx.XeRqhyyy { font-weight: 500; } (+ rule.attributes.component)
    const BOLD_FONT_WEIGHT = '500';
    const BTN_ACTIVE = 1;
    const BTN_INACTIVE = 0;

    const getClosestBoldEl = (rte) => {
        const sel = rte && rte.doc && rte.doc.getSelection && rte.doc.getSelection();
        if (!sel || !sel.rangeCount) return null;
        let node = sel.getRangeAt(0).commonAncestorContainer;
        if (node && node.nodeType === 3) node = node.parentNode;
        return (node && node.closest && node.closest('b, strong')) || null;
    };

    const unwrapElement = (el) => {
        const parent = el && el.parentNode;
        if (!parent) return;
        while (el.firstChild) {
            parent.insertBefore(el.firstChild, el);
        }
        parent.removeChild(el);
    };

    // 색 등 다른 스타일이 남아 있으면 <b>만 풀지 말고 <span>으로 바꿔 클래스를 유지한다.
    const demoteBoldToSpan = (el) => {
        const doc = el.ownerDocument || document;
        const span = doc.createElement('span');
        Array.from(el.attributes).forEach((attr) => {
            span.setAttribute(attr.name, attr.value);
        });
        while (el.firstChild) {
            span.appendChild(el.firstChild);
        }
        el.parentNode.replaceChild(span, el);
    };

    const getBoldClassSelector = (el) => {
        if (!el || !el.classList) return '';
        const detailClass = Array.from(el.classList).find((c) => c.startsWith('detail_span_'));
        const randomClass = Array.from(el.classList).find((c) => c.startsWith('XeRqh'));
        if (!detailClass || !randomClass) return '';
        return `.${detailClass}.${randomClass}`;
    };

    const addBoldClassStyle = (selector) => {
        const rules = editor.addStyle(`${selector}{font-weight:${BOLD_FONT_WEIGHT};}`);
        // 저장/미리보기는 rule.attributes.component 가 1depth 부모 셀렉터일 때만 수집한다.
        // 요소 자신의 셀렉터를 넣으면 캔버스에만 보이고 export 에서 빠진다.
        // (요소를 한 번 클릭하면 StyleManager 가 component 를 부모로 고쳐 줘서 그때부터 저장됨)
        const selected = editor.getSelected();
        const higherComponent = selected ? findOneDepthParent(selected) : null;
        const componentSelector = higherComponent
            ? appEditorStore.getComponentSelectorString(higherComponent)
            : '';
        (Array.isArray(rules) ? rules : [rules]).filter(Boolean).forEach((rule) => {
            if (componentSelector) {
                rule.attributes.component = componentSelector;
            }
        });
    };

    /** @return {boolean} 다른 스타일이 남아 래퍼(클래스)를 유지해야 하면 true */
    const stripFontWeightFromRule = (rule) => {
        if (!rule) return false;
        const nextStyle = {...(rule.attributes.style || {})};
        delete nextStyle['font-weight'];
        if (Object.keys(nextStyle).length === 0) {
            editor.CssComposer.remove(rule);
            return false;
        }
        rule.setStyle(nextStyle);
        return true;
    };

    const customBoldResult = (rte) => {
        const boldEl = getClosestBoldEl(rte);
        if (boldEl) {
            const selector = getBoldClassSelector(boldEl);
            const classRule = selector ? editor.CssComposer.getRule(selector) : null;
            const idRule = boldEl.id ? editor.CssComposer.getRule(`#${boldEl.id}`) : null;
            const keepWrapper =
                stripFontWeightFromRule(classRule) || stripFontWeightFromRule(idRule);

            if (keepWrapper) {
                demoteBoldToSpan(boldEl);
            } else {
                unwrapElement(boldEl);
            }
            return;
        }
        const selected = rte.selection();
        if (!selected) return;
        const detailClass = `detail_span_${createId(5)}`;
        const randomClass = `XeRqh${createId(6)}`;
        const selector = `.${detailClass}.${randomClass}`;
        rte.insertHTML(`<b class="${detailClass} ${randomClass}">${selected}</b>`);
        addBoldClassStyle(selector);
    };

    const customBoldState = (rte) => (getClosestBoldEl(rte) ? BTN_ACTIVE : BTN_INACTIVE);

    // RTE 모듈이 config.actions 배열 참조를 캐싱하므로 제자리에서 교체
    // icon: 기본 B(Bold) 대신 M(Medium, font-weight 500) 표기
    const BOLD_ICON = '<b>M</b>';
    if (Array.isArray(rteConfig.actions)) {
        const boldIdx = rteConfig.actions.findIndex(
            (a) => a === 'bold' || (a && a.name === 'bold')
        );
        if (boldIdx > -1) {
            rteConfig.actions[boldIdx] = {
                name: 'bold',
                icon: BOLD_ICON,
                result: customBoldResult,
                state: customBoldState,
            };
        }
    }
    const liveBold = editor.RichTextEditor.get('bold');
    if (liveBold) {
        liveBold.icon = BOLD_ICON;
        liveBold.result = customBoldResult;
        liveBold.state = customBoldState;
    }

    // Canvas
    const {Canvas} = editor;
    const disablePanning = debounce(() => appEditorStore.setCanvasPanning(false), 200);
    editor.on(Canvas.events.coords, () => {
        appEditorStore.setCanvasPanning(true);
        disablePanning();
    });

    // Commands
    const spotSelectTargetId = 'target-selection';
    editor.Commands.add(CMD_SELECT_TARGET, {
        opts: {},
        init() {
            this.beforeSelect = this.beforeSelect.bind(this);
        },
        onComponentHover(em, cmp) {
            const spot = {id: spotSelectTargetId, type: 'target', component: cmp};
            cmp ? Canvas.addSpot(spot) : Canvas.removeSpots(spot);
        },
        beforeSelect(cmp, opts = {}) {
            opts.abort = true;
            opts.useValid = false;
            this.opts.onSelect(cmp);
            editor.stopCommand(CMD_SELECT_TARGET);
        },
        run(em, _, opts) {
            this.opts = opts;
            editor.on('component:select:before', this.beforeSelect);
            editor.on('change:componentHovered', this.onComponentHover);
        },
        stop() {
            editor.off('component:select:before', this.beforeSelect);
            editor.off('change:componentHovered', this.onComponentHover);
            Canvas.removeSpots({id: spotSelectTargetId});
            appEditorStore.setSelectingTarget(false);
        },
    });

    editor.Commands.add(CMD_ACTIVE_STYLES, () => {
        appEditorStore.setSelectedDesignerTab('style');
    });

    extendExternalComponents(editor);
    blockIconsToComponent(editor);
    fillEmptyComponents(editor, {});
}
